import { unwrapResult } from '@reduxjs/toolkit'
import { useCallback, useRef, useState } from 'react'

import { setExportOrdersPreferences } from '@/atoms'
import { exportOrders, getExportOrderColumns } from '@/features/domain/order'
import { useDelayedCallback, useNotification } from '@/hooks'
import { useAppDispatch } from '@/store'

import {
  columnSelectionAtom,
  setColumnSelection,
  setColumnSelectionExecuted,
} from '../atom/columnSelection'
import { useTexts } from '../useTexts'

import { type Column, type OrderDataStatus, useController } from './useController'
import { useCreateColumns } from './useCreateColumns'

const unique = (ids: string[]) => [...new Set(ids)]

export const useControllerActions = () => {
  const [isLoading, setIsLoading] = useState(false)
  const texts = useTexts()
  const toast = useNotification()
  const dispatch = useAppDispatch()

  const toastId = useRef<string | number>(-1)

  const {
    close,
    updateData,
    data: {
      selectedOrders,
      format,
      keepEnglishColumns,
      columnSelection,
      columnsState,
      customizeColumns,
      includePod,
      orderDataStatus,
    },
  } = useController()

  const createColumns = useCreateColumns()

  const [showToastAndCloseModalAfterTimer, clearToastTimer] = useDelayedCallback(() => {
    toastId.current = toast(texts.preparingExport, {
      autoClose: false,
      closeOnClick: true,
      theme: 'light',
    })
    close?.()
  }, 2000)

  const exportOrder = useCallback(async () => {
    try {
      if (columnSelection.length === 0) {
        updateData({ showEmptySelectionWarning: true })
        return
      }

      setIsLoading(true)

      // remove some possible duplicates
      const orderIds = unique(selectedOrders.map(order => order.order.id))

      if (columnsState.status !== 'complete') {
        throw new Error('columns status must be complete')
      }

      const columnsSet = columnsState.columns.reduce<Record<string, string>>((acc, curr) => {
        acc[curr.id] = curr.label
        return acc
      }, {})

      const requestedColumns = columnSelection.map(columnId => ({
        columnId,
        label: columnsSet[columnId],
      }))

      showToastAndCloseModalAfterTimer()

      const exportRequest = await dispatch(
        exportOrders({
          orderIds,
          format,
          requestedColumns,
          includePod,
          executedOrdersOnly: orderDataStatus === 'execution',
        }),
      )

      // DEBUG
      // await new Promise(resolve => setTimeout(resolve, 5000))

      if (exportOrders.fulfilled.match(exportRequest)) {
        const url = unwrapResult(exportRequest)
        if (url) {
          window.open(url)
        }
      }

      setExportOrdersPreferences({
        format,
        customizeColumns,
        keepEnglishColumns,
      })

      if (orderDataStatus === 'execution') {
        setColumnSelectionExecuted(columnSelection)
      } else {
        setColumnSelection(columnSelection)
      }

      toast.dismiss(toastId.current)
      clearToastTimer()
      close?.()
    } catch (e) {
      toast.error(texts.genericExportOrdersError)
    }

    setIsLoading(false)
  }, [
    close,
    texts,
    toast,
    format,
    columnsState,
    dispatch,
    updateData,
    selectedOrders,
    clearToastTimer,
    columnSelection,
    showToastAndCloseModalAfterTimer,
    customizeColumns,
    keepEnglishColumns,
    includePod,
  ])

  const exportOrderLegacy = useCallback(async () => {
    try {
      setIsLoading(true)

      // remove some possible duplicates
      const orderIds = unique(selectedOrders.map(order => order.order.id))

      showToastAndCloseModalAfterTimer()

      const exportRequest = await dispatch(
        exportOrders({
          orderIds,
          format,
          requestedColumns: undefined,
          includeBarcodes: true,
          includePod,
          executedOrdersOnly: orderDataStatus === 'execution',
        }),
      )

      // DEBUG
      // await new Promise(resolve => setTimeout(resolve, 5000))

      if (exportOrders.fulfilled.match(exportRequest)) {
        const url = unwrapResult(exportRequest)
        if (url) {
          window.open(url)
        }
      }

      setExportOrdersPreferences({
        format,
        customizeColumns: false,
        keepEnglishColumns: false,
      })

      toast.dismiss(toastId.current)
      clearToastTimer()
      close?.()
    } catch (e) {
      toast.error(texts.genericExportOrdersError)
    }

    setIsLoading(false)
  }, [
    close,
    texts,
    toast,
    format,
    dispatch,
    selectedOrders,
    includePod,
    orderDataStatus,
    clearToastTimer,
    showToastAndCloseModalAfterTimer,
  ])

  const onChangeIncludeBarcodes = useCallback(
    (_e, checked) => updateData({ includeBarcodes: checked }),
    [updateData],
  )

  const onChangeCustomizeColumns = useCallback(
    (_e, checked) => updateData({ customizeColumns: checked }),
    [updateData],
  )

  const onChangeFormat = useCallback(
    (_e, value: string) => {
      if (!value) return
      updateData({ format: value as typeof format })
    },
    [updateData],
  )

  const onChangeKeepEnglishColumns = useCallback(
    (keepEnglishColumns: boolean) => {
      updateData({ keepEnglishColumns })
    },
    [updateData],
  )

  const onNextButtonClick = useCallback(async () => {
    try {
      updateData({ columnsState: { status: 'loading' } })

      const orderIds = unique(selectedOrders.map(order => order.order.id))
      const result = await dispatch(
        getExportOrderColumns({
          orderIds,
          executedOrdersOnly: orderDataStatus === 'execution',
        }),
      )
      if (getExportOrderColumns.rejected.match(result)) {
        throw new Error(result.payload?.message ?? 'Internal error')
      }

      const columns = await createColumns(result.payload, keepEnglishColumns, orderDataStatus)

      const columnSelection = replacePlaceholderInsideSelection(
        orderDataStatus === 'execution'
          ? columnSelectionAtom.executedSelection
          : columnSelectionAtom.selection,
        columns,
        orderDataStatus,
      )

      updateData({
        wizardStep: 'configure-columns',
        columnsState: { status: 'complete', columns },
        columnSelection,
      })
    } catch (e) {
      updateData({ columnsState: { status: 'error' } })
      toast.error(texts.genericExportOrdersError)
    }
  }, [
    updateData,
    keepEnglishColumns,
    dispatch,
    createColumns,
    texts,
    toast,
    selectedOrders,
    orderDataStatus,
  ])

  const onPrevButtonClick = useCallback(() => {
    updateData({ wizardStep: 'configure-format' })
  }, [updateData])

  const onColumnSelectionChange = useCallback(
    (columnSelection: string[]) => {
      updateData({ columnSelection, showEmptySelectionWarning: false })
    },
    [updateData],
  )

  return {
    exportOrder,
    exportOrderLegacy,
    onChangeFormat,
    onChangeIncludeBarcodes,
    isLoading,
    onChangeKeepEnglishColumns,
    onNextButtonClick,
    onPrevButtonClick,
    onColumnSelectionChange,
    onChangeCustomizeColumns,
  }
}

function replacePlaceholderInsideSelection(
  selection: readonly string[],
  columns: Column[],
  orderDataStatus: OrderDataStatus,
) {
  const groups = {
    loads: [] as string[],
    pickupCf: [] as string[],
    deliveryCf: [] as string[],
    serviceCf: [] as string[],
    pickupBc: [] as string[],
    deliveryBc: [] as string[],
    serviceBc: [] as string[],
    pickupLoads: [] as string[],
    deliveryLoads: [] as string[],
    serviceLoads: [] as string[],
  }

  const columnsIds = new Set(columns.map(c => c.id))

  for (const { id } of columns) {
    // Custom fields
    if (id.startsWith('CF_D_')) {
      groups.deliveryCf.push(id)
    }
    if (id.startsWith('CF_P_')) {
      groups.pickupCf.push(id)
    }
    if (id.startsWith('CF_S_')) {
      groups.serviceCf.push(id)
    }

    // Loads (configured orders - on the order)
    if (id.startsWith('L_')) {
      groups.loads.push(id)
    }

    // Barcodes
    // NOTE: Barcodes for the executed orders are separated in many columns (e.g. `BC_P_1_...`, `BC_P_2_...`, etc.),
    // instead for the configured orders, all the barcodes are merged into one column per step
    // (called `P_barcodes`, `D_barcodes`, `S_barcodes`).
    if (orderDataStatus === 'execution') {
      if (id.startsWith('BC_P_')) {
        groups.pickupBc.push(id)
      }
      if (id.startsWith('BC_D_')) {
        groups.deliveryBc.push(id)
      }
      if (id.startsWith('BC_S_')) {
        groups.serviceBc.push(id)
      }
    }

    // Loads (executed orders - on the orderstep)
    if (id.startsWith('L_P_')) {
      groups.pickupLoads.push(id)
    }
    if (id.startsWith('L_D_')) {
      groups.deliveryLoads.push(id)
    }
    if (id.startsWith('L_S_')) {
      groups.serviceLoads.push(id)
    }
  }

  const result = selection.reduce<string[]>((acc, id) => {
    // Loads (configured orders - on the order)
    if (id === 'loads') {
      acc.push(...groups.loads)
      return acc
    }

    // Custom fields
    if (id === 'P_customfields') {
      acc.push(...groups.pickupCf)
      return acc
    }

    if (id === 'S_customfields') {
      acc.push(...groups.serviceCf)
      return acc
    }

    if (id === 'D_customfields') {
      acc.push(...groups.deliveryCf)
      return acc
    }

    // Barcodes
    // NOTE: Barcodes for the executed orders are separated in many columns (e.g. `BC_P_1_...`, `BC_P_2_...`, etc.),
    // instead for the configured orders, all the barcodes are merged into one column per step
    // (called `P_barcodes`, `D_barcodes`, `S_barcodes`).
    if (orderDataStatus === 'execution') {
      if (id === 'P_barcodes') {
        acc.push(...groups.pickupBc)
        return acc
      }

      if (id === 'S_barcodes') {
        acc.push(...groups.serviceBc)
        return acc
      }

      if (id === 'D_barcodes') {
        acc.push(...groups.deliveryBc)
        return acc
      }
    }

    // Loads (executed orders - on the orderstep)
    if (id === 'P_loads') {
      acc.push(...groups.pickupLoads)
      return acc
    }

    if (id === 'S_loads') {
      acc.push(...groups.serviceLoads)
      return acc
    }

    if (id === 'D_loads') {
      acc.push(...groups.deliveryLoads)
      return acc
    }

    if (columnsIds.has(id)) {
      acc.push(id)
    }

    return acc
  }, [] as string[])

  return result
}
